export type SectionKey = 'company' | 'product' | 'sales' | 'competitors' | 'team';

/** Mirrors server QuestionnaireImportPayload — keep in sync with questionnaire-schema */
export type QuestionnaireExtractPayload = {
  company?: {
    website?: string | null;
    industry?: string | null;
    revenueModel?: string | null;
    companySize?: string | null;
    marketPosition?: string | null;
  };
  product?: {
    description?: string | null;
    valueProposition?: string | null;
    onboardingProcess?: string | null;
    cost?: string | null;
    pricingDetails?: string | null;
    topFeedbackItems?: string[] | null;
  };
  sales?: {
    targetBuyers?: string | null;
    topBuyerTitles?: string[] | null;
    buyerChannels?: string | null;
    salesProcessTimeline?: { stage: string; order: number }[] | null;
    salesCycle?: string | null;
    majorObjections?: string[] | null;
    objectionHandling?: string | null;
  };
  competitors?: {
    competitors?:
      | {
          name: string;
          website?: string | null;
          differentiators?: string | null;
          pricing?: string | null;
        }[]
      | null;
  };
  team?: {
    teamSize?: number | null;
    seniorityLevel?: string | null;
    avgYearsExperience?: number | null;
  };
  markComplete?: boolean;
};

interface CompanyData {
  website: string;
  industry: string;
  revenueModel: string;
  companySize: string;
  marketPosition: string;
}

interface ProductData {
  description: string;
  valueProposition: string;
  onboardingProcess: string;
  cost: string;
  pricingDetails: string;
  topFeedbackItems: string[];
}

interface SalesData {
  targetBuyers: string;
  topBuyerTitles: string[];
  buyerChannels: string;
  salesProcessTimeline: { stage: string; order: number }[];
  salesCycle: string;
  majorObjections: string[];
  objectionHandling: string;
}

interface CompetitorsData {
  competitors: { name: string; website: string; differentiators: string; pricing: string }[];
}

interface TeamData {
  teamSize: number;
  seniorityLevel: string;
  avgYearsExperience: number;
}

export interface ExtractMapping {
  company: CompanyData;
  product: ProductData;
  sales: SalesData;
  competitors: CompetitorsData;
  team: TeamData;
  updatedSections: SectionKey[];
}

function nonEmptyString(v: string | null | undefined): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

const VALID_SIZES = new Set(['1-10', '11-50', '51-200', '201-500', '500+']);

const MARKET_POSITIONS = new Set(['Leader', 'Challenger', 'Niche', 'Emerging']);

export function mapExtractToQuestionnaire(
  payload: QuestionnaireExtractPayload,
  current: {
    company: CompanyData;
    product: ProductData;
    sales: SalesData;
    competitors: CompetitorsData;
    team: TeamData;
  },
): ExtractMapping {
  const company = { ...current.company };
  const product = { ...current.product };
  const sales = { ...current.sales };
  const competitors = { ...current.competitors, competitors: [...current.competitors.competitors] };
  const team = { ...current.team };

  const updatedSections: SectionKey[] = [];

  const c = payload.company;
  if (c) {
    let changed = false;
    if (nonEmptyString(c.website)) {
      company.website = c.website.trim();
      changed = true;
    }
    if (nonEmptyString(c.industry)) {
      company.industry = c.industry.trim();
      changed = true;
    }
    if (nonEmptyString(c.revenueModel)) {
      company.revenueModel = c.revenueModel.trim();
      changed = true;
    }
    if (nonEmptyString(c.companySize) && VALID_SIZES.has(c.companySize.trim())) {
      company.companySize = c.companySize.trim();
      changed = true;
    }
    if (nonEmptyString(c.marketPosition) && MARKET_POSITIONS.has(c.marketPosition.trim())) {
      company.marketPosition = c.marketPosition.trim();
      changed = true;
    }
    if (changed) updatedSections.push('company');
  }

  const p = payload.product;
  if (p) {
    let changed = false;
    if (nonEmptyString(p.description)) {
      product.description = p.description.trim();
      changed = true;
    }
    if (nonEmptyString(p.valueProposition)) {
      product.valueProposition = p.valueProposition.trim();
      changed = true;
    }
    if (nonEmptyString(p.onboardingProcess)) {
      product.onboardingProcess = p.onboardingProcess.trim();
      changed = true;
    }
    if (nonEmptyString(p.cost)) {
      product.cost = p.cost.trim();
      changed = true;
    }
    if (nonEmptyString(p.pricingDetails)) {
      product.pricingDetails = p.pricingDetails.trim();
      changed = true;
    }
    if (p.topFeedbackItems && Array.isArray(p.topFeedbackItems) && p.topFeedbackItems.length > 0) {
      const items = p.topFeedbackItems.filter((x) => nonEmptyString(x)).slice(0, 5);
      if (items.length > 0) {
        product.topFeedbackItems = items;
        changed = true;
      }
    }
    if (changed) updatedSections.push('product');
  }

  const s = payload.sales;
  if (s) {
    let changed = false;
    if (nonEmptyString(s.targetBuyers)) {
      sales.targetBuyers = s.targetBuyers.trim();
      changed = true;
    }
    if (s.topBuyerTitles && Array.isArray(s.topBuyerTitles) && s.topBuyerTitles.length > 0) {
      const titles = s.topBuyerTitles.filter((x) => nonEmptyString(x)).slice(0, 5);
      if (titles.length > 0) {
        sales.topBuyerTitles = titles;
        changed = true;
      }
    }
    if (nonEmptyString(s.buyerChannels)) {
      sales.buyerChannels = s.buyerChannels.trim();
      changed = true;
    }
    if (
      s.salesProcessTimeline &&
      Array.isArray(s.salesProcessTimeline) &&
      s.salesProcessTimeline.length > 0
    ) {
      const timeline = s.salesProcessTimeline
        .filter((row) => nonEmptyString(row.stage) && typeof row.order === 'number')
        .map((row) => ({ stage: row.stage.trim(), order: row.order }));
      if (timeline.length > 0) {
        sales.salesProcessTimeline = timeline;
        changed = true;
      }
    }
    if (nonEmptyString(s.salesCycle)) {
      sales.salesCycle = s.salesCycle.trim();
      changed = true;
    }
    if (s.majorObjections && Array.isArray(s.majorObjections) && s.majorObjections.length > 0) {
      const objs = s.majorObjections.filter((x) => nonEmptyString(x));
      if (objs.length > 0) {
        sales.majorObjections = objs;
        changed = true;
      }
    }
    if (nonEmptyString(s.objectionHandling)) {
      sales.objectionHandling = s.objectionHandling.trim();
      changed = true;
    }
    if (changed) updatedSections.push('sales');
  }

  const comp = payload.competitors?.competitors;
  if (comp && Array.isArray(comp) && comp.length > 0) {
    const normalized = comp
      .filter((row) => nonEmptyString(row?.name))
      .slice(0, 10)
      .map((row) => ({
        name: row.name.trim(),
        website: nonEmptyString(row.website) ? row.website.trim() : '',
        differentiators: nonEmptyString(row.differentiators) ? row.differentiators.trim() : '',
        pricing: nonEmptyString(row.pricing) ? row.pricing.trim() : '',
      }));
    if (normalized.length > 0) {
      competitors.competitors = normalized;
      updatedSections.push('competitors');
    }
  }

  const t = payload.team;
  if (t) {
    let changed = false;
    if (typeof t.teamSize === 'number' && !Number.isNaN(t.teamSize) && t.teamSize >= 0) {
      team.teamSize = Math.floor(t.teamSize);
      changed = true;
    }
    if (nonEmptyString(t.seniorityLevel)) {
      team.seniorityLevel = t.seniorityLevel.trim();
      changed = true;
    }
    if (
      typeof t.avgYearsExperience === 'number' &&
      !Number.isNaN(t.avgYearsExperience) &&
      t.avgYearsExperience >= 0
    ) {
      team.avgYearsExperience = t.avgYearsExperience;
      changed = true;
    }
    if (changed) updatedSections.push('team');
  }

  return { company, product, sales, competitors, team, updatedSections };
}
